import {
  callGetOrganisation,
  callUpdateOrganisation,
  callGetStatsPostCounter,
  callSendEmailPostPack,
  callGetStatsStudentReach,
  callGetStatsSchools,
  callGetStatsRegions,
  callGetStatsMailouts,
  callGetStatsPostCategories,
  callGetStatsTopPosts,
  callGetStatsOrganisationActivity,
  callGetStatsOrganisationPostShares,
  callGetStatsOrganisationPostReach,
  callGetStatsOrganisationPostCategories,
  callGetStatsPosts,
  callGetStatsPost,
  callGetPosts,
  callGetPost,
  callCreatePost,
  callUpdatePost,
  callDeletePost,
  callSaveFile,
  callSaveVideo,
  callGetResources,
  callGetConnectionsCounter,
  callGetConnections,
  callGetConnection,
  callUpsertConnection,
  callRemoveConnection,
  callSendMessage,
  callRegisterOrganisation,
  callGetStatsOrganisationContactActivity,
  callGetStatsConnections,
  callGetStatsConnection,
  callVerifyPublicProfile,
  callGetProfilePosts,
  callGetProfileConnections,
  callGetProfileConnection,
  callSendProfileConnectionMessage,
  callGetAccounts,
  callUpsertAccount,
  callDeleteAccount,
} from '../../api/contributors.js'
import {
  triggerAlert,
  triggerErrorGeneric,
  isSuccessResponse,
  mapConnection,
} from '../util.js'
import { EventBus } from '../../../js/plugins/event-bus.js'
import { EVENT_KEYS } from '../../util/constants.js'

/************************** OVERVIEW/GENERAL **************************/

/**
 * Initialiase contributor Dashboard page (organisation and visible stats).
 *
 * @param {Object} context state context
 */
export async function dispatchInitPageDashboard(context) {
  await Promise.all([
    context.dispatch('dispatchUpdateStatsPostCounter'),
    context.dispatch('dispatchUpdateStatsStudentReach'),
    context.dispatch('dispatchUpdateStatsSchools'),
    context.dispatch('dispatchUpdateStatsMailouts'),
    context.dispatch('dispatchUpdateStatsPostCategories'),
  ])
}

/**
 * Initialiase contributor Posts page.
 *
 * @param {Object} context state context
 *
 * @returns {Object} posts pagination (default to null)
 */
export async function dispatchInitPagePosts(context, payload) {
  const [pagination] = await Promise.all([
    context.dispatch('dispatchUpdatePosts', payload),
    context.dispatch('dispatchUpdateStatsPostCounter'),
  ])

  return pagination || null
}

/**
 * Initialiase contributor Connections page.
 *
 * @param {Object} context state context
 *
 * @returns {Object} posts pagination (default to null)
 */
export async function dispatchInitPageConnections(context, payload) {
  await context.dispatch('dispatchUpdateConnectionsCounter')
  const pagination = await context.dispatch(
    'dispatchUpdateConnections',
    payload,
  )

  return pagination || null
}

/**
 * Initialiase contributor Settings page.
 *
 * @param {Object} context state context
 */
export async function dispatchInitPageSettings(context) {
  const orgId = context.getters.getOrganisationId
  await context.dispatch('dispatchSetOrganisation', orgId)
}

/**
 * Initialiase contributor Statistics page (both tabs).
 *
 * Note: dispatchUpdateStatsOrganisationActivity needs to happen before
 * dispatchUpdateStatsOrganisationPostCategories because the latter appends
 * data to the same state object
 *
 * @param {Object} context state context
 *
 * @returns {Object} posts pagination (default to null)
 */
export async function dispatchInitPageStats(context) {
  const postsPagination = await context.dispatch('dispatchUpdateStatsPosts')
  await context.dispatch('dispatchUpdateStatsOrganisationActivity')
  await Promise.all([
    context.dispatch('dispatchUpdateStatsPostCounter'),
    context.dispatch('dispatchUpdateStatsOrganisationPostCategories'),
    context.dispatch('dispatchUpdateConnectionsCounter'),
    context.dispatch('dispatchUpdateStatsConnections'),
    context.dispatch('dispatchUpdateStatsConnectionsActivity'),
  ])

  return postsPagination || null
}

/**
 * Send post pack request email.
 *
 * @param {Object} context state context
 * @param {Object} payload post pack data
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchSendEmailPostPack(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callSendEmailPostPack(orgId, payload)

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/************************** ORGANISATIONS **************************/

/**
 * Get/set user organisations.
 *
 * @param {Object} context state context
 * @param {Array} organisations
 *
 **/
export async function dispatchUpdateOrganisations(context, organisations) {
  try {
    if (organisations) {
      context.commit('UPDATE_ORGANISATIONS', organisations)
      await context.dispatch(
        'dispatchSetOrganisation',
        organisations[0]?.id || null,
      )
    } else {
      context.commit('CLEAR_ORGANISATIONS')
    }
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Set selected organisation in state.
 *
 * @param {Object} context state context
 * @param {Object} organisation current selected organisation
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchUpdateOrganisation(context, organisation = null) {
  try {
    if (organisation) {
      context.commit('UPDATE_ORGANISATION', organisation)
    } else {
      context.commit('CLEAR_ORGANISATION')
    }
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Get then set selected organisation.
 *
 * @param {Object} context state context
 * @param {Number} orgId current selected organisation ID
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchSetOrganisation(context, orgId = null) {
  try {
    orgId = orgId || context.getters.getOrganisationId
    const response = await callGetOrganisation(orgId)

    context.dispatch('dispatchUpdateOrganisation', response?.data?.data)

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Update/edit (current) organisation details.
 *
 * @param {Object} context state context
 * @param {Object} payload organisation details
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchEditOrganisation(context, payload) {
  try {
    if (payload) {
      const orgId = context.getters.getOrganisationId
      const response = await callUpdateOrganisation(orgId, payload)
      await context.dispatch('dispatchInitPageSettings')

      triggerAlert(response)
      return isSuccessResponse(response)
    }

    return triggerErrorGeneric()
  } catch {
    triggerErrorGeneric()
  }
}

/************************** STATISTICS **************************/

/**
 * Update the post counter stats.
 *
 * @param {Object} context state context
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchUpdateStatsPostCounter(context) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsPostCounter(orgId)

    if (isSuccessResponse(response)) {
      context.commit('UPDATE_POST_COUNTER', response.data.data)
    }

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Update the student reach stats.
 *
 * @param {Object} context state context
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchUpdateStatsStudentReach(context) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsStudentReach(orgId)

    if (isSuccessResponse(response)) {
      context.commit('UPDATE_STUDENT_REACH', response.data.data)
    }

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Update the organisation schools.
 *
 * @param {Object} context state context
 * @param {Object} payload region id (default to All Regions)
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchUpdateStatsSchools(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsSchools(orgId, payload)

    if (isSuccessResponse(response)) {
      context.commit('UPDATE_SCHOOLS_COUNT', response.data.data?.totalSchools)
      context.commit('UPDATE_SCHOOLS', response.data.data?.schools)
    }

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Update regions for school list.
 *
 * @param {Object} context state context
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchUpdateStatsRegions(context) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsRegions(orgId)

    if (isSuccessResponse(response)) {
      context.commit('UPDATE_REGIONS', response.data.data)
    }

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Update mailouts stats.
 *
 * @param {Object} context state context
 * @param {Object} payload date range
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchUpdateStatsMailouts(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsMailouts(orgId, payload)

    if (isSuccessResponse(response)) {
      context.commit('UPDATE_MAILOUTS', response.data.data)
    }

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Update post categories stats.
 *
 * @param {Object} context state context
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchUpdateStatsPostCategories(
  context,
  payload = null,
) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsPostCategories(orgId, payload)

    if (isSuccessResponse(response)) {
      context.commit('UPDATE_POST_CATEGORIES', response?.data?.data?.categories)
      context.commit(
        'UPDATE_POST_CATEGORIES_COUNT',
        response?.data?.data?.total,
      )
    }

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Update top posts stats.
 *
 * @param {Object} context state context
 * @param {Object} payload date range
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchUpdateStatsTopPosts(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsTopPosts(orgId, payload)

    if (isSuccessResponse(response)) {
      context.commit('UPDATE_TOP_POSTS', response.data.data)
    }

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Update organisation activity stats.
 *
 * @param {Object} context state context
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchUpdateStatsOrganisationActivity(context) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsOrganisationActivity(orgId)

    if (isSuccessResponse(response)) {
      context.commit('UPDATE_ORGANISATION_ACTIVITY', response.data.data)
    }

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Update organisation total post shares stats.
 *
 * @param {Object} context state context
 * @param {Object} payload date range
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchUpdateStatsOrganisationPostShares(
  context,
  payload = null,
) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsOrganisationPostShares(orgId, payload)

    if (isSuccessResponse(response)) {
      context.commit('UPDATE_ORGANISATION_POST_SHARES', response.data.data)
    }

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Update organisation total post reach stats.
 *
 * @param {Object} context state context
 * @param {Object} payload date range
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchUpdateStatsOrganisationPostReach(
  context,
  payload = null,
) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsOrganisationPostReach(orgId, payload)

    if (isSuccessResponse(response)) {
      context.commit('UPDATE_ORGANISATION_POST_REACH', response.data.data)
    }

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Update organisation post categories stats.
 *
 * @param {Object} context state context
 * @param {Object} payload date range
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchUpdateStatsOrganisationPostCategories(
  context,
  payload = null,
) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsOrganisationPostCategories(
      orgId,
      payload,
    )

    if (isSuccessResponse(response)) {
      context.commit(
        'UPDATE_ORGANISATION_POST_CATEGORIES',
        response.data.data?.categories,
      )
      context.commit(
        'UPDATE_ORGANISATION_POST_CATEGORIES_COUNT',
        response.data.data?.total,
      )
    }

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Update posts with stats.
 *
 * @param {Object} context state context
 * @param {Object} payload pagination
 *
 * @returns {Object} posts pagination (default to null)
 */
export async function dispatchUpdateStatsPosts(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsPosts(orgId, payload)

    if (isSuccessResponse(response)) {
      context.commit('UPDATE_POSTS_STATS', response.data.data?.posts)
    }

    triggerAlert(response)
    return response?.data?.data?.pagination || null
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Get post stats (by post id).
 *
 * @param {Object} context state context
 * @param {Object} payload post id
 *
 * @returns {Object} post stats (default to null)
 */
export async function dispatchGetStatsPost(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsPost(orgId, payload)

    triggerAlert(response)
    return response?.data?.data || null
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Initialiase contributor Statistics page Connections tab - overall/total connections activity stats.
 *
 * @param {Object} context state context
 */
export async function dispatchUpdateStatsConnectionsActivity(context) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsOrganisationContactActivity(orgId)

    if (response?.data?.success) {
      const data = response.data.data
      context.commit('UPDATE_CONNECTIONS_IMPRESSIONS', data.impressions)
      context.commit('UPDATE_CONNECTIONS_VIEWS', data.views)
      context.commit('UPDATE_CONNECTIONS_CONTACTED', data.contacted)
    } else {
      context.commit('CLEAR_CONNECTIONS_IMPRESSIONS')
      context.commit('CLEAR_CONNECTIONS_VIEWS')
      context.commit('CLEAR_CONNECTIONS_CONTACTED')
    }

    triggerAlert(response)
  } catch {
    triggerErrorGeneric()
    context.commit('CLEAR_CONNECTIONS_IMPRESSIONS')
    context.commit('CLEAR_CONNECTIONS_VIEWS')
    context.commit('CLEAR_CONNECTIONS_CONTACTED')
  }
}

/**
 * Initialiase contributor Statistics page Connections tab - connections-specific stats.
 *
 * @param {Object} context state context
 * @param {Object} payload includes pagination
 *
 * @returns {Object} connections pagination (default to null)
 */
export async function dispatchUpdateStatsConnections(context, payload) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsConnections(orgId, payload)

    if (response?.data?.success) {
      const data = response.data.data
      const connections = data?.contacts
      const pagination = data?.pagination
      context.commit('UPDATE_CONNECTIONS_STATS', connections)
      context.commit('UPDATE_CONNECTIONS_PAGINATION', pagination)
    } else {
      context.commit('CLEAR_CONNECTIONS_STATS')
      context.commit('CLEAR_CONNECTIONS_PAGINATION')
    }

    triggerAlert(response)
  } catch {
    triggerErrorGeneric()
    context.commit('CLEAR_CONNECTIONS_STATS')
    context.commit('CLEAR_CONNECTIONS_PAGINATION')
  }
}

/**
 * Get connection stats (by connection id) - for individual connection modal.
 *
 * @param {Object} context state context
 * @param {Object} payload connection id
 *
 * @returns {Object} connection stats (default to null)
 */
export async function dispatchGetStatsConnection(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetStatsConnection(orgId, payload)

    triggerAlert(response)
    return response?.data?.data || null
  } catch {
    triggerErrorGeneric()
  }
}

/************************** POSTS **************************/

/**
 * Update posts.
 *
 * @param {Object} context state context
 * @param {Object} payload pagination, filters
 *
 * @returns {Object} posts pagination (default to null)
 */
export async function dispatchUpdatePosts(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetPosts(orgId, payload)

    if (isSuccessResponse(response)) {
      const posts = response.data.data?.posts
      posts.forEach((post) => {
        //general tags
        let general_arr = []
        context.rootState.shared.settings.tags.forEach((i) => {
          if (post.tag_titles.includes(i.text)) {
            general_arr.push(i.text)
          }
        })
        post.general_tags = general_arr

        // industry tags
        let industry_arr = []
        context.rootState.shared.settings.tag_industries.forEach((i) => {
          if (post.tag_titles.includes(i.text)) {
            industry_arr.push(i.text)
          }
        })
        post.industry_tags = industry_arr

        // Year tags
        let year_arr = []
        context.rootState.shared.settings.tag_years.forEach((i) => {
          if (post.tag_titles.includes(i.text)) {
            year_arr.push(i.text)
          }
        })
        post.year_tag = year_arr

        // Region tags
        let region_arr = []
        context.rootState.shared.settings.tag_regions.forEach((i) => {
          if (post.tag_titles.includes(i.text)) {
            region_arr.push(i.text)
          }
        })
        post.region_tag = region_arr
      })
      context.commit('UPDATE_POSTS', posts)
    }

    triggerAlert(response)
    return response?.data?.data?.pagination || null
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Get/view post.
 *
 * @param {Object} context state context
 * @param {Number} postId
 *
 * @returns {Object} post data (default to null)
 * @todo handle return vals/errors here once all api calls are moved out of components
 */
export async function dispatchGetPost(context, postId = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetPost(orgId, postId)

    context.dispatch('shared/dispatchSetCurrentPost', response?.data?.data, {
      root: true,
    })
    return response?.data?.data || null
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Create a new post.
 *
 * @param {Object} context state context
 * @param {Object} payload new post
 *
 * @returns {Object} response data (default to null)
 */
export async function dispatchCreatePost(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callCreatePost(orgId, payload)

    if (isSuccessResponse(response)) {
      // refresh posts data
      await context.dispatch('dispatchUpdatePosts')
    }

    triggerAlert(response)
    return response?.data || null
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Update an existing post.
 *
 * @param {Object} context state context
 * @param {Object} payload updated post
 *
 * @returns {Object} response data (default to null)
 */
export async function dispatchUpdatePost(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callUpdatePost(orgId, payload)
    if (response.data?.success) {
      EventBus.$emit('update_post_success', payload.post_id)
    }

    triggerAlert(response)
    return response?.data || null
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Delete a post.
 *
 * @param {Object} context state context
 * @param {Object} payload post id
 *
 * @returns {Object} response data (default to null)
 */
export async function dispatchDeletePost(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callDeletePost(orgId, payload)

    if (isSuccessResponse(response)) {
      await context.dispatch('dispatchUpdatePosts')
      EventBus.$emit('post-delete', response)
    }

    triggerAlert(response)
    return response?.data || null
  } catch {
    triggerErrorGeneric()
  }
}

/************************** RESOURCES **************************/

/**
 * Update resources.
 *
 * @param {Object} context state context
 * @param {Object} payload pagination, filters
 *
 * @returns {Object} resources pagination (default to null)
 */
export async function dispatchGetResources(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetResources(orgId, payload)

    triggerAlert(response)
    return response || null
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Create a new post.
 *
 * @param {Object} context state context
 * @param {Object} payload new resource
 *
 * @returns {Object} response data (default to null)
 */
export async function dispatchSaveFile(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callSaveFile(orgId, payload)

    triggerAlert(response)
    return response?.data || null
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Create a new post.
 *
 * @param {Object} context state context
 * @param {Object} payload new resource
 *
 * @returns {Object} response data (default to null)
 */
export async function dispatchSaveVideo(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callSaveVideo(orgId, payload)

    triggerAlert(response)
    return response?.data || null
  } catch {
    triggerErrorGeneric()
  }
}

/************************** CONNECTIONS **************************/

/**
 * Update the connections counter.
 *
 * @param {object} context
 * @returns {object|boolean} data or api failure
 */
export async function dispatchUpdateConnectionsCounter(context) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetConnectionsCounter(orgId)

    if (isSuccessResponse(response)) {
      context.commit('UPDATE_CONNECTIONS_COUNTER', response.data.data)
    } else {
      context.commit('CLEAR_CONNECTIONS_COUNTER')
    }

    triggerAlert(response)
    return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Gets the connections for this organisation/contributor, according to filters.
 *
 * @param {object} context
 * @param {object} payload includes pagination (page), filters
 * @returns {object|boolean} data or api failure
 */
export async function dispatchUpdateConnections(context, payload) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetConnections(orgId, payload)

    if (response?.data?.success) {
      const data = response.data.data?.data?.map(mapConnection)
      context.commit('UPDATE_CONNECTIONS', data)
      triggerAlert(response)
      return response.data.data
    }

    context.commit('CLEAR_CONNECTIONS')
    triggerAlert(response)
    return false
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Gets full details/info for this organisation/contributor connection.
 *
 * @param {object} context
 * @param {object} payload includes 'connectionId'
 * @returns {*} connection details, if successful; null, if unsuccessful
 */
export async function dispatchUpdateConnection(context, payload) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callGetConnection(orgId, payload.connectionId)

    if (response?.data?.success) {
      const data = mapConnection(response.data?.data)
      triggerAlert(response)
      return data
    }

    triggerAlert(response)
    return null
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Creates or updates an organisation/contributor connection.
 *
 * @param {object} context
 * @param {object} payload includes connection details
 * @returns {boolean} api success/failure
 */
export async function dispatchUpsertConnection(context, payload) {
  try {
    const orgId = context.getters.getOrganisationId
    const response = await callUpsertConnection(
      orgId,
      payload.id || null,
      payload.formData,
    )

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Removes (soft deletes) an organisation/contributor connection from CareerWise.
 * Response includes pagination, emitted back to the parent page.
 *
 * @param {object} context
 * @param {object} payload includes connectionId
 * @returns {boolean} api success/failure
 */
export async function dispatchRemoveConnection(context, payload) {
  try {
    const orgId = context.getters.getOrganisationId
    const connectionId = payload?.connectionId || null
    const response = await callRemoveConnection(orgId, connectionId)

    const connectionsResponse = await context.dispatch(
      'dispatchInitPageConnections',
    )

    EventBus.$emit(
      EVENT_KEYS.CO_UPDATE_COMPONENT_PAGINATION,
      connectionsResponse,
    )

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Send connection pack request email.
 *
 * @param {Object} context state context
 * @param {Object} payload connection pack data
 *
 * @returns {Boolean} success/failure
 */
export async function dispatchSendEmailConnectionPack(context, payload = null) {
  try {
    const orgId = context.getters.getOrganisationId
    return
    // @TODO - Connections: implement
    // const response = await callSendEmailConnectionPack(orgId, payload)

    // triggerAlert(response)
    // return isSuccessResponse(response)
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Sends an email to a connection from contributor context. Backend logs the activity.
 *
 * @param {object} context
 * @param {object} payload includes connectionId, message details, userId to log
 * @returns {boolean} api success/failure
 */
export async function dispatchSendMessage(context, payload) {
  try {
    const orgId = context.getters.getOrganisationId
    const connectionId = payload?.connectionId || null
    const response = await callSendMessage(orgId, connectionId, payload)

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Register (sign up) an organisation.
 *
 * @param {object} context
 * @param {object} payload includes organisation name, email, and comments
 * @returns {boolean} api success/failure
 */
export async function dispatchRegisterOrganisation(_context, payload) {
  try {
    const response = await callRegisterOrganisation(payload)

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}

/************************** ACCOUNTS/MANAGERS/ADMINS **************************/

/**
 * Get organisation accounts (managers/admins).
 *
 * @param {object} context
 * @param {object} payload includes connectionsOnly flag
 * @returns {boolean} api success/failure
 */
export async function dispatchUpdateAccounts(context, payload) {
  try {
    const orgId = context.getters.getOrganisationId

    if (orgId) {
      const response = await callGetAccounts(orgId, payload)

      triggerAlert(response)
      if (response?.data?.success) {
        const data = response.data.data
        context.commit('UPDATE_ACCOUNTS', data)
        return true
      }

      context.commit('CLEAR_ACCOUNTS')
      return false
    }

    context.commit('CLEAR_ACCOUNTS')
    triggerErrorGeneric()
  } catch {
    context.commit('CLEAR_ACCOUNTS')
    triggerErrorGeneric()
  }
}

/**
 * Upsert an organisation account (manager/admin).
 *
 * @param {object} context
 * @param {object} payload account data, may include id
 * @returns {boolean} api success/failure
 */
export async function dispatchUpsertAccount(context, payload) {
  try {
    const orgId = context.getters.getOrganisationId

    if (orgId) {
      const response = await callUpsertAccount(orgId, payload)

      triggerAlert(response)
      if (response?.data?.success) {
        return true
      }

      return false
    }

    triggerErrorGeneric()
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Delete an organisation account (manager/admin).
 *
 * @param {object} context
 * @param {object} payload account data including account ID
 * @returns {boolean} api success/failure
 */
export async function dispatchDeleteAccount(context, payload) {
  try {
    const orgId = context.getters.getOrganisationId

    if (orgId) {
      const response = await callDeleteAccount(orgId, payload.id)

      triggerAlert(response)
      if (response?.data?.success) {
        return true
      }

      return false
    }

    triggerErrorGeneric()
  } catch {
    triggerErrorGeneric()
  }
}

/************************** PUBLIC PROFILE **************************/

/**
 * Verify an organisation has a public profile.
 *
 * @param {object} context
 * @param {object} payload includes organisation name
 * @returns {boolean} api success/failure
 */
export async function dispatchVerifyPublicProfile(context, payload) {
  try {
    const response = await callVerifyPublicProfile(payload)

    triggerAlert(response) // @TODO not working because of redirect
    if (response?.data?.success) {
      const data = response.data.data
      context.commit('UPDATE_ORGANISATION_PROFILE', data)
      return true
    } else {
      context.commit('CLEAR_ORGANISATION_PROFILE')
      return false
    }
  } catch {
    context.commit('CLEAR_ORGANISATION_PROFILE')
    triggerErrorGeneric()
  }
}

/**
 * Get public organisation posts.
 *
 * @param {object} context
 * @param {object} payload includes filters, pagination
 * @returns {boolean} api success/failure
 */
export async function dispatchUpdateProfilePosts(context, payload) {
  try {
    const orgId = context.getters.getOrganisationProfileId

    if (orgId) {
      const response = await callGetProfilePosts(orgId, payload)

      triggerAlert(response)
      if (response?.data?.success) {
        const data = response.data.data
        const postsData = data.posts
        const paginationData = data.pagination
        context.commit('UPDATE_ORGANISATION_PROFILE_POSTS', postsData)
        context.commit(
          'UPDATE_ORGANISATION_PROFILE_POSTS_PAGINATION',
          paginationData,
        )
        return true
      } else {
        context.commit('CLEAR_ORGANISATION_PROFILE_POSTS')
        context.commit('CLEAR_ORGANISATION_PROFILE_POSTS_PAGINATION')
        return false
      }
    }

    context.commit('CLEAR_ORGANISATION_PROFILE_POSTS')
    context.commit('CLEAR_ORGANISATION_PROFILE_POSTS_PAGINATION')
    triggerErrorGeneric()
  } catch {
    context.commit('CLEAR_ORGANISATION_PROFILE_POSTS')
    context.commit('CLEAR_ORGANISATION_PROFILE_POSTS_PAGINATION')
    triggerErrorGeneric()
  }
}

/**
 * Get public organisation connections.
 *
 * @param {object} context
 * @param {object} payload includes filters
 * @returns {boolean} api success/failure
 */
export async function dispatchUpdateProfileConnections(context, payload) {
  try {
    const orgId = context.getters.getOrganisationProfileId

    if (orgId) {
      const response = await callGetProfileConnections(orgId, payload)

      triggerAlert(response)
      if (response?.data?.success) {
        const data = response.data.data?.map(mapConnection)
        context.commit('UPDATE_ORGANISATION_PROFILE_CONNECTIONS', data)
        return true
      }

      context.commit('CLEAR_ORGANISATION_PROFILE_CONNECTIONS')
      return false
    }

    context.commit('CLEAR_ORGANISATION_PROFILE_CONNECTIONS')
    triggerErrorGeneric()
  } catch {
    context.commit('CLEAR_ORGANISATION_PROFILE_CONNECTIONS')
    triggerErrorGeneric()
  }
}

/**
 * Gets full details/info for this public connection.
 *
 * @param {object} context
 * @param {object} payload 'connectionId'
 * @returns {boolean} api success/failure
 */
export async function dispatchUpdateProfileConnection(context, payload) {
  try {
    const orgId = context.getters.getOrganisationProfileId

    if (orgId) {
      const response = await callGetProfileConnection(
        orgId,
        payload.connectionId,
      )

      triggerAlert(response)
      if (response?.data?.success) {
        const data = mapConnection(response.data?.data)
        return data
      }

      return null
    }

    triggerErrorGeneric()
    return null
  } catch {
    triggerErrorGeneric()
  }
}

/**
 * Sends an email to a connection from public contributor profile context. Backend logs the activity.
 *
 * @param {object} context
 * @param {object} payload includes connectionId, message details
 * @returns {boolean} api success/failure
 */
export async function dispatchSendProfileConnectionMessage(context, payload) {
  try {
    const orgId = context.getters.getOrganisationId
    const connectionId = payload?.connectionId || null
    const response = await callSendProfileConnectionMessage(
      orgId,
      connectionId,
      payload,
    )

    triggerAlert(response)
    return !!response?.data?.success
  } catch {
    triggerErrorGeneric()
  }
}
